/**
 * Shared money figure renderer (E2.1 design spec 4.4, formatting rule 11.3):
 * wraps formatMoney with tabular-nums (theme.ts's MONEY_NUMERIC_SX)
 * plus size/weight props, so every dashboard figure renders through one
 * place instead of each card hand-rolling the same sx. Does not retrofit
 * older pages (spec 4.4) -- only new dashboard figures use this.
 */
import Typography, { type TypographyProps } from '@mui/material/Typography';

import { useDisplayCurrency } from '../currency/CurrencyContext.tsx';
import { MONEY_NUMERIC_SX } from '../theme.ts';

export interface MoneyTextProps {
  /** Integer minor units (toea); formatted via formatMoney. */
  minor: number;
  currencyCode: string | null;
  /** Typography font-size token or CSS size; default '1rem'. */
  size?: string;
  /** Typography font-weight; default 700. */
  weight?: number;
  component?: TypographyProps['component'];
  sx?: TypographyProps['sx'];
}

export function MoneyText({
  minor,
  currencyCode,
  size = '1rem',
  weight = 700,
  component,
  sx,
}: Readonly<MoneyTextProps>): React.JSX.Element {
  const { format } = useDisplayCurrency();
  return (
    <Typography
      {...(component ? { component } : {})}
      sx={{ ...MONEY_NUMERIC_SX, fontSize: size, fontWeight: weight, ...sx }}
    >
      {format(minor, currencyCode)}
    </Typography>
  );
}

export default MoneyText;
